'use client';
import React, { useEffect, useRef, useState } from 'react';

interface FreightProgram {
  rank: number;
  name: string;
  type: string;
  dispatchFee: string;
  avgWeeklyGross: string;
  avgWeeklyNet: string;
  keyFeature: string;
  badge: string;
}

const programs: FreightProgram[] = [
  {
    rank: 1,
    name: 'Amazon Relay Contract Lanes',
    type: 'OTR / Regional',
    dispatchFee: '10% Dispatch Fee',
    avgWeeklyGross: '$8,000–$11,000',
    avgWeeklyNet: '$5,000–$7,000',
    keyFeature: 'Scheduled freight · Drop & Hook · Amazon trailers',
    badge: '90% Gross Revenue',
  },
  {
    rank: 2,
    name: 'Street & Spot Loads',
    type: 'Regional / Flexible',
    dispatchFee: '12% Dispatch Fee',
    avgWeeklyGross: 'Market Rate',
    avgWeeklyNet: 'Varies by Lane',
    keyFeature: 'Flexible lanes · Closer to home · More home time',
    badge: 'More Flexibility',
  },
];

interface ProgramHighlight {
  icon: string;
  title: string;
  description: string;
}

const amazonHighlights: ProgramHighlight[] = [
  { icon: '📦', title: 'Drop & Hook', description: 'Amazon provides the trailer — already loaded when you arrive. Drop the current trailer, hook to a preloaded one.' },
  { icon: '📅', title: 'Scheduled Blocks', description: 'A block is a scheduled work period (~37 hrs). Your schedule is provided before the contract begins.' },
  { icon: '💰', title: 'Pre-Negotiated Rates', description: 'Example: $5,334/week divided into 3 blocks at $1,778/block. Rate paid upon block completion.' },
  { icon: '🚛', title: 'Power Only', description: 'We are a power-only operation. No company trailers — Amazon provides trailers on contract freight.' },
];

const streetSpotHighlights: ProgramHighlight[] = [
  { icon: '🏠', title: 'Home More Often', description: 'Potentially home once or twice a week depending on available lanes and market conditions.' },
  { icon: '🗺️', title: 'Regional Operations', description: 'Prefer operating within a specific area? Street & Spot may be a better fit for your schedule.' },
  { icon: '⚡', title: 'Load Flexibility', description: 'More control over lane selection and operating area. Choose loads that fit your preferred routes.' },
  { icon: '📍', title: 'Stay Closer to Home', description: 'Avoid 2–3 week OTR stretches. Regional and local freight options when available.' },
];

const ScoreBar: React.FC<{ score: number; animated: boolean }> = ({ score, animated }) => {
  const pct = (score / 5) * 100;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-plum-mid rounded-full overflow-hidden">
        <div
          className="h-full bg-violet rounded-full transition-all duration-1000 ease-out"
          style={{ width: animated ? `${pct}%` : '0%' }}
        />
      </div>
      <span className="text-[11px] font-bold text-silver-lavender/70 w-6 text-right">{score}</span>
    </div>
  );
};

const CarrierRankings: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [animated, setAnimated] = useState(false);
  const revealRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimated(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('visible');
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    revealRefs.current.forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <section id="rankings" className="py-32 relative" ref={sectionRef}>
      <div className="beam-border-h" style={{ top: 0, bottom: 'auto' }} />
      <div className="dot-pattern absolute inset-0 opacity-20 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div
          className="reveal mb-16 grid md:grid-cols-12 gap-8"
          ref={(el) => { revealRefs.current[0] = el; }}>
          <div className="md:col-span-1">
            <span className="section-num">01/</span>
          </div>
          <div className="md:col-span-7 space-y-4">
            <p className="text-[11px] font-bold uppercase tracking-[0.4em] text-violet">
              Freight Programs
            </p>
            <h2 className="text-display font-bold tracking-tight leading-tight text-cream">
              Choose the Program
              <br />
              <span className="text-silver-lavender/60">That Fits Your Business.</span>
            </h2>
          </div>
          <div className="md:col-span-4 flex items-end">
            <p className="text-base text-silver-lavender/60 leading-relaxed font-light">
              We offer multiple freight programs so you can choose the operating model
              that fits your business, income goals, and preferred home-time schedule.
            </p>
          </div>
        </div>

        {/* Program cards */}
        <div
          className="grid lg:grid-cols-2 gap-8 mb-16 reveal"
          ref={(el) => { revealRefs.current[1] = el; }}>
          {programs.map((p) => (
            <div
              key={p.name}
              className="border border-silver-lavender/15 rounded-2xl overflow-hidden bg-plum-deep/40 hover:border-violet/30 transition-all duration-300">
              {/* Card header */}
              <div className="bg-plum-deep/80 px-6 py-5 border-b border-silver-lavender/10 flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-violet/70 bg-violet/10 px-2.5 py-1 rounded-full">
                      {p.type}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-cream">{p.name}</h3>
                  <p className="text-sm text-silver-lavender/50 mt-1">{p.dispatchFee}</p>
                </div>
                <div className="shrink-0 text-right">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-violet bg-violet/15 border border-violet/25 px-3 py-1.5 rounded-full whitespace-nowrap">
                    {p.badge}
                  </span>
                </div>
              </div>

              {/* Earnings */}
              <div className="px-6 py-4 grid grid-cols-2 gap-4 border-b border-silver-lavender/8">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-silver-lavender/40 mb-1">Avg Weekly Gross</p>
                  <p className="text-xl font-bold text-cream">{p.avgWeeklyGross}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-silver-lavender/40 mb-1">Avg Weekly Net</p>
                  <p className="text-xl font-bold text-violet">{p.avgWeeklyNet}</p>
                </div>
              </div>

              {/* Key feature */}
              <div className="px-6 py-4">
                <p className="text-sm text-silver-lavender/60 font-light">{p.keyFeature}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Highlights grid */}
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Amazon Relay highlights */}
          <div
            className="reveal"
            ref={(el) => { revealRefs.current[2] = el; }}>
            <div className="mb-6">
              <h3 className="text-lg font-bold text-cream mb-1">Amazon Relay Contract Details</h3>
              <p className="text-sm text-silver-lavender/50 font-light">
                How contract freight works — scheduled blocks, Drop & Hook, and pre-negotiated rates.
              </p>
            </div>
            <div className="space-y-4">
              {amazonHighlights.map((h) => (
                <div key={h.title} className="flex gap-4 bg-plum-card border border-silver-lavender/10 rounded-xl p-4 hover:border-violet/20 transition-colors">
                  <span className="text-2xl shrink-0">{h.icon}</span>
                  <div>
                    <p className="text-sm font-bold text-cream mb-1">{h.title}</p>
                    <p className="text-sm text-silver-lavender/55 font-light leading-relaxed">{h.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Street & Spot highlights */}
          <div
            className="reveal reveal-delay-2"
            ref={(el) => { revealRefs.current[3] = el; }}>
            <div className="mb-6">
              <h3 className="text-lg font-bold text-cream mb-1">Street & Spot Load Details</h3>
              <p className="text-sm text-silver-lavender/50 font-light">
                More flexibility, more control over home time — for drivers who prefer to stay closer to home.
              </p>
            </div>
            <div className="space-y-4">
              {streetSpotHighlights.map((h) => (
                <div key={h.title} className="flex gap-4 bg-plum-card border border-silver-lavender/10 rounded-xl p-4 hover:border-violet/20 transition-colors">
                  <span className="text-2xl shrink-0">{h.icon}</span>
                  <div>
                    <p className="text-sm font-bold text-cream mb-1">{h.title}</p>
                    <p className="text-sm text-silver-lavender/55 font-light leading-relaxed">{h.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Disclaimer */}
            <div className="mt-5 bg-plum-deep/60 border border-silver-lavender/10 rounded-xl p-4">
              <p className="text-xs text-silver-lavender/40 leading-relaxed font-light italic">
                Actual earnings may vary depending on the contract, operating area, fuel costs, truck expenses, miles, and other business expenses. Freight availability, rates, and home-time options depend on current market conditions and available lanes.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CarrierRankings;